import { Button, Col, Divider, Grid, Row, Tooltip, Typography } from 'antd'
import React, { useEffect } from 'react'
import { Controller, useController, useForm } from 'react-hook-form'
import FilterDateInputs from '@shared/DateInput'
import { useDispatch } from 'react-redux'
import { fetchBusinessDate } from 'store/actions/configs'
import dateFilterSchema from '../../models/datefilter.schema'
import { yupResolver } from '@hookform/resolvers/yup'
import SelectGlobal from '@shared/Select'
import { DateLimitIcon, DownloadExcelFile } from '@modules/CustomIcons'
import { useTranslation } from 'react-i18next'
import {
  ExclamationCircleOutlined,
} from '@ant-design/icons'
import loadingData from 'assets/images/nodata.png'
import nodata from 'assets/images/nodata1.png'
import ComputerPhoto from '../ComputerPhoto'
import { CheckCircleOutlined } from '@mui/icons-material'
import { useExcelExportMutation } from 'store/reducers/acquiring/excelExport.api'

function downloadBase64FileUsingBlob({ fileBase64, fileName, mimeType }: {
  fileBase64: string;
  fileName: string;
  mimeType: string;
}) {
  const byteCharacters = atob(fileBase64);

  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);

  const blob = new Blob([byteArray], { type: mimeType });

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}


function PosReportingFilterBar() {
  const { t: translate } = useTranslation()
  const { xs } = Grid.useBreakpoint()
  const dispatch = useDispatch()

  const [mutate, { isLoading, isError, isSuccess, reset }] = useExcelExportMutation()

  useEffect(() => {
    dispatch(fetchBusinessDate())
  }, [])

  const { setValue, control, handleSubmit } = useForm({
    resolver: yupResolver(dateFilterSchema),
    defaultValues: {
      dates: {
        endDate: '',
        startDate: '',
        changeType: 'segment',
      },
      language: 'AZ',
    },
  })

  const {
    fieldState: { invalid: startDateInValid, error: startDateError },
  } = useController({ control, name: 'dates.startDate' })

  const {
    fieldState: { invalid: endDateInValid, error: endError },
  } = useController({ control, name: 'dates.endDate' })

  const language = [
    {
      value: 'AZ',
      label: 'AZ',
    },
    {
      value: 'EN',
      label: 'EN',
    },
    {
      value: 'RU',
      label: 'RU',
    },
  ]

  const onSubmit = async (requestData: any) => {
    const {
      language,
      dates: { startDate, endDate, },
    } = requestData
    try {
      const data = await mutate({ startDate, endDate, language  }).unwrap();
      downloadBase64FileUsingBlob(data)
    } catch (error) {}
  }

  useEffect(() => {
    let timeId;
    if(isSuccess || isError) {
      timeId = setTimeout(() => {
        reset()
      }, 5000)
    }
    return () => clearTimeout(timeId)
  }, [isSuccess, isError])

  return (
    <>
      <FilterDateInputs
        vertical
        startDateError={startDateInValid}
        startDateErrorMessage={startDateError?.message}
        endDateError={endDateInValid}
        endDateErrorMessage={endError?.message}
        value="yesterday"
        onChangePicker={(value) => {
          setValue(
            'dates',
            {
              startDate: value.dateFrom
                ? value.dateFrom.format('DD.MM.YYYY')
                : '',
              endDate: value.dateTo ? value.dateTo.format('DD.MM.YYYY') : '',
              changeType: value.changeType,
            },
            {
              shouldValidate: true,
            }
          )
        }}
        element={
          <Col>
            <Row justify={xs ? 'space-between' : 'start'} style={{ gap: 20 }}>
              {xs && (
                <Tooltip
                  placement={xs ? 'right' : 'bottom'}
                  title={translate('acquiring.note')}
                >
                  <Row
                    style={{
                      width: 40,
                      height: 40,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: '#D9E8F7',
                      borderRadius: 8,
                    }}
                  >
                    <DateLimitIcon />
                  </Row>
                </Tooltip>
              )}
              <Row
                style={{
                  border: '1px solid #D9E3EC',
                  borderRadius: '12px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-evenly',
                  padding: '6px 10px',
                  gap: 8,
                  boxSizing: 'border-box',
                }}
              >
                <Controller
                  name="language"
                  control={control}
                  defaultValue="az"
                  render={({ field }) => (
                    <SelectGlobal
                      selectType
                      allowClear={false}
                      size="small"
                      options={language}
                      {...field}
                      style={{
                        margin: 0,
                        border: 'none',
                        borderRadius: '10px',
                        boxShadow: '0px 1px 12px 0px #00000026',
                      }}
                    />
                  )}
                />
                <Tooltip title={translate('transfers.downloadExcel')}>
                  <Button
                    type="default"
                    icon={<DownloadExcelFile />}
                    size="large"
                    style={{
                      border: 'none',
                      borderRadius: '50%',
                      boxShadow: '0px 1px 12px 0px #00000026',
                      width: '32px',
                      height: '32px',
                    }}
                    onClick={handleSubmit(onSubmit)}
                  />
                </Tooltip>
              </Row>
            </Row>
          </Col>
        }
      />

      <Divider type="horizontal" />
      {
        <ComputerPhoto
          img={isLoading ? loadingData : nodata}
          element={[
            isSuccess && (
              <Row align="middle" style={{ gap: 8 }}>
                <CheckCircleOutlined
                  style={{ fontSize: '18px', color: '#0fbf1b' }}
                />
                <Typography.Text>
                  {translate('acquiring.successMessage')}
                </Typography.Text>
              </Row>
            ),
            isError && (
              <Row align="middle" style={{ gap: 8 }}>
                <ExclamationCircleOutlined
                  style={{ fontSize: '18px', color: '#ff4e57' }}
                />
                <Typography.Text>
                  {translate('accountStatement.noTransactionDuringPeriodNote')}
                </Typography.Text>
              </Row>
            ),
          ]}
        />
      }
    </>
  )
}

export default PosReportingFilterBar
